import { useMemo, useState } from 'react';
import { Zap } from 'lucide-react';
import useCreditPacks from '../../hooks/useCreditPacks';
import PackBoosterCheckoutModal from '../stripe/PackBoosterCheckoutModal';

/**
 * PackBoosterCTA
 *
 * Visibility logic :
 *   - Shown when the per-thematique sujet cap is near / reached (lifetime),
 *     or when any *monthly* quota (custom_mono / custom_prompt) is at
 *     ≥ 80 % of its cap / exceeded.
 *   - Hidden for admins/reviewers (bypass).
 *   - Hidden during initial load.
 *
 * Props :
 *   - thematiqueId : number — scopes doc credits to that thematique.
 *   - sujetQuota   : { used, max } — lifetime sujet cap from customStore.quota.
 */
export default function PackBoosterCTA({ thematiqueId = null, sujetQuota = null, onPurchased }) {
    const packs = useCreditPacks(thematiqueId);
    const [open, setOpen] = useState(false);

    const trigger = useMemo(() => {
        if (packs.loading) return null;

        // Lifetime sujet cap (enforced in upload.php / generate-from-prompt.php).
        const sujetRatio    = sujetQuota && sujetQuota.max > 0 ? sujetQuota.used / sujetQuota.max : 0;
        const sujetExceeded = sujetQuota && sujetQuota.used >= sujetQuota.max;

        // Lifetime mono cap per thematique (from /user/credit-packs.php).
        const monoCap       = packs.monoCap;
        const monoUsed      = packs.monoUsed;
        const monoRatio     = monoCap && monoCap > 0 ? monoUsed / monoCap : 0;
        const monoExceeded  = monoCap != null && monoUsed >= monoCap;

        // Priority : exceeded first, then near-warning.
        // 'sujet'  = capacité de la thématique (tous sujets confondus, IA ou non)
        // 'mono'   = crédits de génération IA (questions générées une par une)
        if (sujetExceeded && packs.docRemaining <= 0) {
            return { kind: 'exceeded', type: 'sujet' };
        }
        if (monoExceeded && packs.monoRemaining <= 0) {
            return { kind: 'exceeded', type: 'mono' };
        }
        if (sujetRatio >= 0.8 && !sujetExceeded) {
            return { kind: 'warn', type: 'sujet' };
        }
        if (monoRatio >= 0.8 && !monoExceeded) {
            return { kind: 'warn', type: 'mono' };
        }
        return null;
    }, [packs, sujetQuota]);

    if (!trigger) return null;

    const messages = {
        sujet: {
            exceeded: 'Vous avez utilisé toutes vos générations IA pour cette thématique. Achetez un Pack IA pour en débloquer plus.',
            warn:     'Vous approchez de votre quota de générations IA pour cette thématique.',
        },
        mono: {
            exceeded: 'Vos crédits de génération IA (questions à l’unité) sont épuisés. Achetez un Pack IA pour en générer plus.',
            warn:     'Vos crédits de génération IA (questions à l’unité) approchent du quota.',
        },
    };
    const msg = messages[trigger.type][trigger.kind];

    return (
        <>
            <div className={`pack-booster-cta pack-booster-cta--${trigger.kind}`}>
                <div className="pack-booster-cta__icon"><Zap size={16} /></div>
                <div className="pack-booster-cta__text">
                    <div className="pack-booster-cta__msg">{msg}</div>
                    <div className="pack-booster-cta__detail">
                        Pack IA {packs.priceEur}€ — +{packs.content.doc} places/générations IA · +{packs.content.mono} questions à l’unité.
                        {packs.packs.length > 0 && <> Vous avez déjà {packs.packs.length} pack{packs.packs.length > 1 ? 's' : ''} actif{packs.packs.length > 1 ? 's' : ''}.</>}
                    </div>
                </div>
                <button className="btn btn-sm btn-primary" onClick={() => setOpen(true)}>
                    <Zap size={13} /> Acheter — {packs.priceEur}€
                </button>
            </div>
            {open && (
                <PackBoosterCheckoutModal
                    thematiqueId={thematiqueId}
                    offers={packs.offers}
                    priceEur={packs.priceEur}
                    content={packs.content}
                    onClose={() => setOpen(false)}
                    onSuccess={() => { packs.reload(); onPurchased?.(); }}
                />
            )}
        </>
    );
}
